import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Select,
  Grid2 as Grid,
  MenuItem,
  FormHelperText,
  FormControl,
  Grid2Props,
  Typography,
} from "@mui/material";
import { singleGridItem } from "../defaultStyles";
import { useFormHandlerContext } from "../../../../controllers/formController";
import { useInputFullName } from "../subForms/NamePrefixContext";
import UserAttributeMetadataPaper from "../../attributeMetadata/UserAttributeMetadataPaper";
import FormLabel from "../../common/base/FormLabel";
import { useFetchUserEnums } from "../../../../hooks/query/useUserEnumQuery";

export interface FormSelectFieldProps extends Grid2Props {
  name?: string;
  disabled?: boolean;
  label: string;
  options?: { value: any; label: string }[];
  useEnum?: string | string[];
  selfFetching?: boolean;
  required?: boolean;
  multiple?: boolean;
  placeholder?: string;
  actions?: React.ReactNode;
  validate?: (value: any) => string | boolean;
}

const FormSelectField: React.FC<FormSelectFieldProps> = ({
  name,
  label,
  actions,
  options,
  useEnum,
  required,
  disabled,
  multiple = false,
  selfFetching = false,
  placeholder,
  validate,
  ...grid
}) => {
  const { isProcessing } = useFormHandlerContext();
  const { control } = useFormContext();
  const { userEnums } = useFormHandlerContext();
  const fullName = useInputFullName(name);
  const fetchUserEnums = useFetchUserEnums(
    selfFetching && useEnum
      ? Array.isArray(useEnum)
        ? useEnum
        : [useEnum]
      : []
  );

  const inferredOptions = useEnum
    ? [
        ...new Set(
          Array.isArray(useEnum)
            ? useEnum.flatMap(
                (enumKey) =>
                  userEnums[enumKey] ||
                  (fetchUserEnums.data && fetchUserEnums.data[enumKey]) ||
                  []
              )
            : userEnums[useEnum] ||
              (fetchUserEnums.data && fetchUserEnums.data[useEnum]) ||
              []
        ),
      ]
        .map((value) => ({
          value,
          label: value === "" ? "None" : value,
        }))
        .filter((option) => !multiple || option.value)
    : [];

  const finalOptions = options || inferredOptions;

  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return (
        <Typography variant="body1" color="text.secondary">
          {placeholder || `Select ${label}`}
        </Typography>
      );
    }

    if (multiple && Array.isArray(selected)) {
      return selected
        .map(
          (value) =>
            finalOptions.find((option) => option.value === value)?.label ||
            value
        )
        .join(", ");
    }

    return (
      finalOptions.find((option) => option.value === selected)?.label ||
      selected
    );
  };

  return (
    <Grid {...singleGridItem} {...grid} hidden={grid.hidden}>
      <UserAttributeMetadataPaper name={fullName}>
        <Controller
          name={fullName}
          control={control}
          rules={{
            required: required ? `${label} is required` : false,
            validate,
          }}
          render={({ field, fieldState }) => (
            <>
              <FormLabel
                required={required}
                label={label}
                name={fullName}
                actions={actions}
              />
              <FormControl
                fullWidth
                error={
                  !!fieldState.error || (selfFetching && fetchUserEnums.isError)
                }
              >
                <Select
                  {...field}
                  value={
                    Array.isArray(field.value)
                      ? field.value.filter((value) =>
                          finalOptions.some((option) => option.value === value)
                        )
                      : finalOptions.some(
                            (option) => option.value === field.value
                          )
                        ? field.value
                        : ""
                  }
                  labelId={`${fullName}-label`}
                  id={fullName}
                  multiple={multiple}
                  disabled={disabled || isProcessing}
                  fullWidth
                  displayEmpty
                  renderValue={renderValue}
                >
                  {finalOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.value === null ? (
                        <Typography fontStyle="italic" color="text.secondary">
                          {option.label}
                        </Typography>
                      ) : (
                        option.label
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                )}
                {selfFetching && fetchUserEnums.isLoading && (
                  <FormHelperText>Loading options...</FormHelperText>
                )}
                {selfFetching && fetchUserEnums.isError && (
                  <FormHelperText>
                    Failed to fetch options.&nbsp;
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => fetchUserEnums.refetch()}
                    >
                      Try again
                    </Typography>
                    .
                  </FormHelperText>
                )}
              </FormControl>
            </>
          )}
        />
      </UserAttributeMetadataPaper>
    </Grid>
  );
};

export default FormSelectField;
